<script setup lang="ts">
import { twMerge } from 'tailwind-merge';
import { computed, unref } from 'vue';

// Variant Type
type Variant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'ul'
  | 'ol'
  | 'li'
  | 'md'
  | 'base'
  | 'sm'
  | 'xs'
  | 'xxs'
  | 'NuxtLink';

// Font Weight Type
type Weight = 'thin' | 'extralight' | 'normal' | 'semibold' | 'bold' | 'extrabold' | 'medium';

// NuxtLink Props Types
type NuxtLinkProps = {
  to?: string;
};

// All Props Types
type Props = NuxtLinkProps & {
  variant?: Variant;
  as?: string;
  appendClass?: string;
  tsTitle?: boolean;
  text?: string;
  weight?: Weight;
  size?: 'md' | 'base' | 'sm' | 'xs' | 'xxs';
  align?: 'left' | 'center' | 'right' | 'justify';
  removeMargin?: boolean;
  spnDataTestId?: string;
  dataTestid?: string;
};

// All props
const props = withDefaults(defineProps<Props>(), {
  as: 'p',
  spnDataTestId: '',
  to: undefined,
  variant: undefined,
  appendClass: '',
  text: '',
  weight: undefined,
  size: undefined,
  align: 'left',
  dataTestid: 'atom-typography',
});

// Devices
const { isMobile, isTablet } = useDevice();
const device = computed(() => {
  if (unref(isMobile)) return 'mobile';
  if (unref(isTablet)) return 'tablet';
  return 'desktop';
});

const is_heading = computed(() => {
  const heading_tags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

  return (
    heading_tags.includes(props.as?.toLowerCase() as any) ||
    heading_tags.includes(props.variant?.toLowerCase() as any)
  );
});

const custom_tag = computed(() => {
  if (is_heading.value) {
    return props.as?.toLowerCase();
  }

  // Allowed Tags
  const allowed_tags = [
    'small',
    'kbd',
    'span',
    'div',
    'label',
    'b',
    'strong',
    'i',
    'u',
    'ol',
    'ul',
    'li',
    'a',
    'em',
  ];

  if (props.as === 'NuxtLink') {
    return resolveComponent('NuxtLink');
  }

  return allowed_tags.includes(props.as?.toLowerCase() as any) ? props.as?.toLowerCase() : 'p';
});

const responsive_classes: Record<DeviceType, Record<ResponsiveClassKey, string>> = {
  mobile: {
    h1: 'text-[24px] leading-[36px] mb-3',
    h2: 'text-[20px] leading-[30px] mb-4',
    h3: 'text-[18px] leading-[24px] mb-2',
    h4: 'text-[16px] leading-[20px] mb-3',
    h5: 'text-[14px] leading-[18px]',
    h6: 'text-[12px] leading-[14px]',
    ol: 'list-decimal ml-8  mb-6',
    ul: 'list-disc ml-8  mb-6',
    li: 'text-[16px] leading-[24px] mb-3',
    md: 'text-[18px] leading-[28px]',
    base: 'text-[16px] leading-[24px]',
    sm: 'text-[14px] leading-[20px]',
    xs: 'text-[12px] leading-[18px]',
    xxs: 'text-[10px] leading-[14px]',
  },
  tablet: {
    h1: 'text-[24px] leading-[36px] mb-3',
    h2: 'text-[20px] leading-[30px] mb-4',
    h3: 'text-[18px] leading-[24px] mb-2',
    h4: 'text-[16px] leading-[20px] mb-3',
    h5: 'text-[14px] leading-[18px]',
    h6: 'text-[12px] leading-[14px]',
    ol: 'list-decimal ml-8  mb-6',
    ul: 'list-disc ml-8  mb-6',
    li: 'text-[16px] leading-[24px] mb-3',
    md: 'text-[18px] leading-[28px]',
    base: 'text-[16px] leading-[24px]',
    sm: 'text-[14px] leading-[20px]',
    xs: 'text-[12px] leading-[18px]',
    xxs: 'text-[10px] leading-[14px]',
  },
  desktop: {
    h1: 'text-[28px] leading-[42px] mb-3',
    h2: 'text-[24px] leading-[36px] mb-4',
    h3: 'text-[20px] leading-[30px] mb-2',
    h4: 'text-[18px] leading-[26px] mb-3',
    h5: 'text-[16px] leading-[24px]',
    h6: 'text-[14px] leading-[20px]',
    ol: 'list-decimal ml-8  mb-6',
    ul: 'list-disc ml-8  mb-6',
    li: 'text-[16px] leading-[24px] mb-3',
    md: 'text-[18px] leading-[28px]',
    base: 'text-[16px] leading-[24px]',
    sm: 'text-[14px] leading-[20px]',
    xs: 'text-[12px] leading-[18px]',
    xxs: 'text-[10px] leading-[14px]',
  },
};

const font_weights = {
  thin: 'font-thin',
  extralight: 'font-extralight',
  normal: 'font-normal',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
  medium: 'font-medium',
};

const text_aligns = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

// Type-safe helper to get responsive class
const getResponsiveClass = (key?: string): string => {
  if (!key) return '';
  // Check if the key is a valid ResponsiveClassKey
  const current_responsive_classes = responsive_classes[device.value];
  if (key in current_responsive_classes) {
    return current_responsive_classes[key as ResponsiveClassKey];
  }
  return '';
};

const heading_classes = computed(() => {
  const variantClass = props.variant ? getResponsiveClass(props.variant) : '';
  const asClass = props.as ? getResponsiveClass(props.as) : '';
  return `font-semibold text-natural-black ${is_heading.value ? variantClass || asClass : ''}`;
});
const base_classes = computed(() => {
  return `font-normal text-natural-grey ${(responsive_classes as any)[device.value]['base']}`;
});

const typo_classes = computed(() => {
  return twMerge(
    is_heading.value ? heading_classes.value : base_classes.value,
    getResponsiveClass(props.as),
    getResponsiveClass(props.variant),
    getResponsiveClass(props.size),
    props.weight ? font_weights[props.weight] : undefined,
    props.align ? text_aligns[props.align] : undefined,
    props.tsTitle && 'ts-title relative pl-3',
    props.removeMargin && 'm-0',
    props.appendClass
  );
});
</script>

<template>
  <component :is="custom_tag" :class="typo_classes" :to="props.to" :data-testid="props.dataTestid">
    <slot>
      <template v-if="props.text">
        <span
          :data-testid="
            props.spnDataTestId ? props.spnDataTestId + '-span' : 'typography-text-span'
          "
          v-html="props.text.replace(/<\/p>/g, '</span></br>').replace(/<p>/g, '<span>')"
        ></span>
      </template>
    </slot>
  </component>
</template>
